import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkflowStore } from '@/stores/workflowStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, Upload, Trash2, Copy, Pencil } from 'lucide-react';
import { WorkflowExportImport } from './WorkflowExportImport';
import { WorkflowCloneDialog } from './WorkflowCloneDialog';
import { FlowOpenButtons } from '@/components/flows/FlowOpenButtons';
import { exportAllWorkflows, exportWorkflowAsJSON } from '@/services/workflowExport';
import { missionMapHref } from '@/lib/appRoutes';
import { toast } from 'sonner';
import type { Workflow } from '@/stores/workflowStore';

export function WorkflowManager() {
  const navigate = useNavigate();
  const { workflows, currentWorkflow, saveWorkflow, deleteWorkflow, importWorkflow, setCurrentWorkflow, updateWorkflowName } =
    useWorkflowStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; workflowId: string; name: string }>({
    open: false,
    workflowId: '',
    name: '',
  });
  const [cloneDialog, setCloneDialog] = useState<{
    open: boolean;
    workflowId: string | null;
    workflowName: string;
  }>({ open: false, workflowId: null, workflowName: '' });

  const handleExportCurrent = () => {
    if (!currentWorkflow) {
      toast.error('No workflow selected');
      return;
    }
    try {
      exportWorkflowAsJSON(currentWorkflow);
      toast.success(`Exported "${currentWorkflow.name}"`);
    } catch (error) {
      toast.error('Failed to export workflow');
    }
  };

  const handleExportAll = () => {
    if (workflows.length === 0) {
      toast.error('No workflows to export');
      return;
    }
    try {
      exportAllWorkflows(workflows);
      toast.success(`Exported ${workflows.length} workflows`);
    } catch (error) {
      toast.error('Failed to export workflows');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      importWorkflow(data);
      toast.success(`Imported workflow "${data.name}"`);
    } catch (error) {
      toast.error('Failed to import workflow');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteWorkflow = (workflowId: string) => {
    const workflow = workflows.find((w) => w.id === workflowId);
    if (!workflow) return;

    if (confirm(`Delete workflow "${workflow.name}"?`)) {
      deleteWorkflow(workflowId);
      toast.success('Workflow deleted');
    }
  };

  const openFlowMap = (workflow: Workflow) => {
    setCurrentWorkflow(workflow);
    navigate(missionMapHref(workflow.id));
  };

  const handleSelectWorkflow = (workflowId: string) => {
    const workflow = workflows.find((w) => w.id === workflowId);
    if (workflow) openFlowMap(workflow);
  };

  const handleCloneClick = (workflowId: string, workflowName: string) => {
    setCloneDialog({ open: true, workflowId, workflowName });
  };

  const openRename = (workflowId: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenameDialog({ open: true, workflowId, name });
  };

  const confirmRename = () => {
    const trimmed = renameDialog.name.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      return;
    }
    const wf = workflows.find((w) => w.id === renameDialog.workflowId);
    if (!wf) return;
    setCurrentWorkflow(wf);
    updateWorkflowName(trimmed);
    saveWorkflow();
    setRenameDialog({ open: false, workflowId: '', name: '' });
    toast.success('Flow renamed');
  };

  const closeCloneDialog = () => {
    setCloneDialog({ open: false, workflowId: null, workflowName: '' });
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" onClick={handleExportCurrent} disabled={!currentWorkflow}>
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
        <Button size="sm" onClick={handleExportAll} disabled={workflows.length === 0}>
          <Download className="w-4 h-4 mr-2" />
          Export All
        </Button>
        <Button size="sm" onClick={handleImportClick} disabled={isImporting}>
          <Upload className="w-4 h-4 mr-2" />
          {isImporting ? 'Importing...' : 'Import'}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Workflows List */}
      <div className="space-y-2">
        {workflows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No workflows yet. Create one to get started.</p>
          </div>
        ) : (
          workflows.map((workflow) => (
            <Card
              key={workflow.id}
              className={`p-4 cursor-pointer ui-lift-card ${
                currentWorkflow?.id === workflow.id
                  ? 'border-primary bg-primary/5'
                  : 'hover:border-primary/50'
              }`}
              onClick={() => handleSelectWorkflow(workflow.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectWorkflow(workflow.id);
                }
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {workflow.name}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                    <span>{workflow.nodes.length} blocks</span>
                    <span>{workflow.edges.length} links</span>
                    <span>
                      {new Date(workflow.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <FlowOpenButtons
                    flowId={workflow.id}
                    onBeforeNavigate={() => setCurrentWorkflow(workflow)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => openRename(workflow.id, workflow.name, e)}
                    aria-label="Rename flow"
                  >
                    <Pencil className="w-4 h-4 text-muted-foreground" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloneClick(workflow.id, workflow.name);
                    }}
                    aria-label="Duplicate flow"
                  >
                    <Copy className="w-4 h-4 text-primary" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteWorkflow(workflow.id);
                    }}
                    aria-label="Delete flow"
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Export/Import Section */}
      <div className="pt-4 border-t border-border">
        <WorkflowExportImport />
      </div>

      <WorkflowCloneDialog
        workflowId={cloneDialog.workflowId}
        workflowName={cloneDialog.workflowName}
        open={cloneDialog.open}
        onOpenChange={(open) => setCloneDialog({ ...cloneDialog, open })}
      />

      <Dialog open={renameDialog.open} onOpenChange={(open) => setRenameDialog({ ...renameDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename flow</DialogTitle>
          </DialogHeader>
          <Input
            value={renameDialog.name}
            onChange={(e) => setRenameDialog({ ...renameDialog, name: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && confirmRename()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog({ open: false, workflowId: '', name: '' })}>
              Cancel
            </Button>
            <Button onClick={confirmRename}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}